import { VoiceTextarea } from '../components/VoiceTextarea';
import { VoiceInput } from '../components/VoiceInput';
// src/modules/CqcReadiness.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Shield, CheckCircle, AlertTriangle, Clock, FileText,
  FileCheck, FileWarning, BookOpen, Users, Home,
  Building, Scale, Award, Crown, User, Calendar, Plus,
  Trash2, Edit, Save, X, Eye, EyeOff, Upload, Image,
  Lock, Unlock, Clipboard
} from 'lucide-react';
import BodyMap, { BodyPart, Annotation } from './BodyMap';
import { CompetencyActionPlan, PlanStatus, DailyChecks, WeeklyChecks, MonthlyChecks } from '../types/compliance';

// ─── TYPES FOR STAFF COMPETENCY ────────────────────────────

interface QuestionResponseEntry {
  question_id: string;
  question_text: string;
  staff_actual_answer: string;
  evaluation_score: 1 | 2 | 3 | 4;
  witnessed_evidence_urls: string[];
  assessor_feedback: string;
}

interface StaffCompetencySession {
  session_id: string;
  date_executed: string;
  interviewee_staff_id: string;
  interviewee_name: string;
  interviewee_role: 'Care Assistant' | 'Domestic/Housekeeping' | 'Kitchen Staff' | 'Maintenance' | 'Nurse';
  interviewer_name: string;
  interviewer_role: 'Home Manager' | 'Deputy Manager' | 'IPC Lead';
  supervising_authority: 'Leeds' | 'Bradford' | 'Kirklees' | 'Wakefield';
  responses: QuestionResponseEntry[];
  session_overall_score: number;
  session_status: 'Fully Compliant (Pass)' | 'Remediation Required (Fail)';
  is_locked_for_cqc: boolean;
}

// ─── READINESS ITEMS ────────────────────────────────────────

interface ReadinessItem {
  id: string;
  category: 'Governance' | 'Safe' | 'Effective' | 'Caring' | 'Responsive' | 'Well-led';
  title: string;
  description: string;
  status: 'Completed' | 'In Progress' | 'Not Started' | 'Overdue';
  lastUpdated: string;
  evidence?: string;
}

const DEFAULT_ITEMS: ReadinessItem[] = [
  // ... (keep your existing default items)
];

// ─── HELPERS ──────────────────────────────────────────────

const generateId = () => Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
const getScoreLabel = (score: number) => {
  if (score === 4) return 'Outstanding (Pass)';
  if (score === 3) return 'Good (Pass)';
  if (score === 2) return 'Partial (Fail)';
  return 'Critical Fail';
};
const getCorrectiveAction = (qId: string): string => {
  const map: Record<string, string> = {
    Q1: 'Staff must undergo 1:1 reassessment on BICSc Color Coding protocols. Daily supervisor sign-offs mandatory for 3 days.',
    Q2: 'Staff must complete IPC refresher on EN 14476 virucidal disinfectants and chlorine dilution. Supervised practice session required.',
    Q3: 'Staff must be trained on COSHH regulations and decanting procedures. Practical assessment on Bottle for Life usage.',
    Q4: 'Staff must demonstrate ability to locate Safety Data Sheets (SDS) in both digital and physical stores. Emergency drill to be performed.',
    Q5: 'Staff must shadow a nurse to perform a physical mattress foam fluid‑strike check. Upload photographic proof of inspection.',
  };
  return map[qId] || 'Retraining required as per local authority guidelines.';
};

// ─── STAFF COMPETENCY COMPONENT ─────────────────────────────

const StaffCompetency: React.FC<{ user: any }> = ({ user }) => {
  // ─── STATE ──────────────────────────────────────────────

  const [sessions, setSessions] = useState<StaffCompetencySession[]>([]);
  const [actionPlans, setActionPlans] = useState<CompetencyActionPlan[]>([]);

  const [showNewSessionForm, setShowNewSessionForm] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | 'warning' } | null>(null);

  const [newSession, setNewSession] = useState<Partial<StaffCompetencySession>>({
    interviewee_staff_id: '',
    interviewee_name: '',
    interviewee_role: 'Care Assistant',
    interviewer_name: user?.name || '',
    interviewer_role: 'Home Manager',
    supervising_authority: 'Wakefield',
    responses: [
      {
        question_id: 'custom-1',
        question_text: 'What is the correct procedure for handling a medication error?',
        staff_actual_answer: '',
        evaluation_score: 3,
        witnessed_evidence_urls: [],
        assessor_feedback: '',
      },
    ],
  });

  const [showActionPlanDrawer, setShowActionPlanDrawer] = useState(false);
  const [pendingSession, setPendingSession] = useState<StaffCompetencySession | null>(null);
  const [actionPlanForm, setActionPlanForm] = useState<Partial<CompetencyActionPlan>>({});

  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  // ─── LOAD / SAVE ──────────────────────────────────────────

  useEffect(() => {
    const storedSessions = localStorage.getItem('rmacs_competency_sessions');
    if (storedSessions) {
      try { setSessions(JSON.parse(storedSessions)); } catch { /* ignore */ }
    } else {
      localStorage.setItem('rmacs_competency_sessions', JSON.stringify([]));
    }
    const storedPlans = localStorage.getItem('rmacs_competency_remediation');
    if (storedPlans) {
      try { setActionPlans(JSON.parse(storedPlans)); } catch { /* ignore */ }
    } else {
      localStorage.setItem('rmacs_competency_remediation', JSON.stringify([]));
    }
  }, []);

  const saveSessions = (data: StaffCompetencySession[]) => {
    localStorage.setItem('rmacs_competency_sessions', JSON.stringify(data));
    setSessions(data);
  };

  const saveActionPlans = (data: CompetencyActionPlan[]) => {
    localStorage.setItem('rmacs_competency_remediation', JSON.stringify(data));
    setActionPlans(data);
  };

  // ─── HANDLERS ──────────────────────────────────────────────

  const handleResponseChange = (index: number, field: keyof QuestionResponseEntry, value: any) => {
    const newResponses = [...(newSession.responses || [])];
    (newResponses[index] as any)[field] = value;
    setNewSession({ ...newSession, responses: newResponses });
  };

  const handleScoreChange = (index: number, score: 1 | 2 | 3 | 4) => {
    handleResponseChange(index, 'evaluation_score', score);
  };

  const handleFileUpload = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const urls = Array.from(files).map(file => URL.createObjectURL(file));
    const current = newSession.responses?.[index]?.witnessed_evidence_urls || [];
    handleResponseChange(index, 'witnessed_evidence_urls', [...current, ...urls]);
  };

  const removeFile = (index: number, fileIndex: number) => {
    const current = newSession.responses?.[index]?.witnessed_evidence_urls || [];
    const updated = current.filter((_, i) => i !== fileIndex);
    handleResponseChange(index, 'witnessed_evidence_urls', updated);
  };

  const resetForm = () => {
    setNewSession({
      interviewee_staff_id: '',
      interviewee_name: '',
      interviewee_role: 'Care Assistant',
      interviewer_name: user?.name || '',
      interviewer_role: 'Home Manager',
      supervising_authority: 'Wakefield',
      responses: [
        {
          question_id: 'custom-1',
          question_text: 'What is the correct procedure for handling a medication error?',
          staff_actual_answer: '',
          evaluation_score: 3,
          witnessed_evidence_urls: [],
          assessor_feedback: '',
        },
      ],
    });
    setShowNewSessionForm(false);
  };

  const addQuestion = () => {
    const newId = `custom-${(newSession.responses || []).length + 1}`;
    const newResponses = [...(newSession.responses || [])];
    newResponses.push({
      question_id: newId,
      question_text: 'Enter your question here...',
      staff_actual_answer: '',
      evaluation_score: 3,
      witnessed_evidence_urls: [],
      assessor_feedback: '',
    });
    setNewSession({ ...newSession, responses: newResponses });
  };

  const removeQuestion = (index: number) => {
    if ((newSession.responses || []).length <= 1) return;
    const newResponses = [...(newSession.responses || [])];
    newResponses.splice(index, 1);
    setNewSession({ ...newSession, responses: newResponses });
  };

  const saveInterview = () => {
    if (!newSession.interviewee_name || !newSession.interviewee_staff_id) {
      setMessage({ text: 'Please fill in staff ID and name.', type: 'error' });
      return;
    }

    const responses = newSession.responses || [];
    const passCount = responses.filter(r => r.evaluation_score >= 3).length;
    const failCount = responses.filter(r => r.evaluation_score < 3).length;
    const total = responses.length;
    const score = (passCount / total) * 100;
    const status = failCount === 0 ? 'Fully Compliant (Pass)' : 'Remediation Required (Fail)';

    const session: StaffCompetencySession = {
      session_id: generateId(),
      date_executed: new Date().toISOString(),
      interviewee_staff_id: newSession.interviewee_staff_id!,
      interviewee_name: newSession.interviewee_name!,
      interviewee_role: newSession.interviewee_role!,
      interviewer_name: newSession.interviewer_name!,
      interviewer_role: newSession.interviewer_role!,
      supervising_authority: newSession.supervising_authority!,
      responses: responses.map(r => ({ ...r })),
      session_overall_score: score,
      session_status: status,
      is_locked_for_cqc: false,
    };

    if (failCount > 0) {
      setPendingSession(session);
      const failedQuestions = responses.filter(r => r.evaluation_score < 3);
      const failedIds = failedQuestions.map(r => r.question_id);
      const deficiencyDetails = failedQuestions.map(r =>
        `Question ${r.question_id}: ${r.question_text} – Score: ${r.evaluation_score} (${getScoreLabel(r.evaluation_score)})`
      ).join('\n');
      const correctiveActions = failedQuestions.map(r =>
        getCorrectiveAction(r.question_id)
      ).join('\n');

      setActionPlanForm({
        parent_session_id: session.session_id,
        failed_question_ids: failedIds,
        staff_id: session.interviewee_staff_id,
        staff_name: session.interviewee_name,
        deficiency_details: deficiencyDetails,
        corrective_action_required: correctiveActions,
        assigned_mentor_name: '',
        daily_verification_checks: {
          day_1_signed: false, day_1_notes: '',
          day_2_signed: false, day_2_notes: '',
          day_3_signed: false, day_3_notes: '',
        },
        weekly_manager_review: {
          week_1_passed: false, week_1_verified_by: '', week_1_notes: '',
          week_2_passed: false, week_2_verified_by: '', week_2_notes: '',
        },
        monthly_governance_signoff: {
          is_signed_off: false,
          signed_off_by_manager: '',
          sign_off_timestamp: null,
          final_evidence_document_url: null,
        },
        plan_status: 'Active Remediation',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        due_dates: {
          daily: [
            new Date(Date.now() + 86400000).toISOString().slice(0,10),
            new Date(Date.now() + 172800000).toISOString().slice(0,10),
            new Date(Date.now() + 259200000).toISOString().slice(0,10),
          ],
          weekly: [
            new Date(Date.now() + 604800000).toISOString().slice(0,10),
            new Date(Date.now() + 1209600000).toISOString().slice(0,10),
          ],
          monthly: new Date(Date.now() + 2592000000).toISOString().slice(0,10),
        },
        // ─── NEW ESCALATION FIELDS ──────────────────────
        actual_completed_at: null,
        actual_chased_at: null,
        chase_deadline: new Date(Date.now() + 4 * 24 * 60 * 60 * 1000).toISOString(),
        closure_deadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      });
      setShowActionPlanDrawer(true);
    } else {
      const updated = [...sessions, session];
      saveSessions(updated);
      setMessage({ text: 'Competency assessment passed!', type: 'success' });
      setTimeout(() => setMessage(null), 3000);
      resetForm();
    }
  };

  const saveActionPlan = () => {
    if (!pendingSession) return;
    if (!actionPlanForm.assigned_mentor_name) {
      setMessage({ text: 'Please assign a mentor name.', type: 'error' });
      return;
    }

    const plan: CompetencyActionPlan = {
      action_id: generateId(),
      parent_session_id: actionPlanForm.parent_session_id!,
      failed_question_ids: actionPlanForm.failed_question_ids!,
      staff_id: actionPlanForm.staff_id!,
      staff_name: actionPlanForm.staff_name!,
      deficiency_details: actionPlanForm.deficiency_details!,
      corrective_action_required: actionPlanForm.corrective_action_required!,
      assigned_mentor_name: actionPlanForm.assigned_mentor_name!,
      daily_verification_checks: {
        day_1_signed: false, day_1_notes: '',
        day_2_signed: false, day_2_notes: '',
        day_3_signed: false, day_3_notes: '',
      },
      weekly_manager_review: {
        week_1_passed: false, week_1_verified_by: '', week_1_notes: '',
        week_2_passed: false, week_2_verified_by: '', week_2_notes: '',
      },
      monthly_governance_signoff: {
        is_signed_off: false,
        signed_off_by_manager: '',
        sign_off_timestamp: null,
        final_evidence_document_url: null,
      },
      plan_status: 'Active Remediation',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      due_dates: {
        daily: [
          new Date(Date.now() + 86400000).toISOString().slice(0,10),
          new Date(Date.now() + 172800000).toISOString().slice(0,10),
          new Date(Date.now() + 259200000).toISOString().slice(0,10),
        ],
        weekly: [
          new Date(Date.now() + 604800000).toISOString().slice(0,10),
          new Date(Date.now() + 1209600000).toISOString().slice(0,10),
        ],
        monthly: new Date(Date.now() + 2592000000).toISOString().slice(0,10),
      },
      // ─── NEW ESCALATION FIELDS ──────────────────────
      actual_completed_at: null,
      actual_chased_at: null,
      chase_deadline: new Date(Date.now() + 4 * 24 * 60 * 60 * 1000).toISOString(),
      closure_deadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    };

    const sessionToSave = { ...pendingSession, is_locked_for_cqc: true };
    const updatedSessions = [...sessions, sessionToSave];
    saveSessions(updatedSessions);

    const updatedPlans = [...actionPlans, plan];
    saveActionPlans(updatedPlans);

    setMessage({ text: 'Action plan created and session locked.', type: 'success' });
    setTimeout(() => setMessage(null), 3000);
    setShowActionPlanDrawer(false);
    setPendingSession(null);
    resetForm();
  };

  // ─── VERIFICATION HANDLERS ─────────────────────────────────

  const handleDailyCheck = (planId: string, day: 'day_1' | 'day_2' | 'day_3', signed: boolean, notes: string) => {
    const updatedPlans = actionPlans.map(p => {
      if (p.action_id === planId) {
        const updated = { ...p };
        (updated.daily_verification_checks as any)[day + '_signed'] = signed;
        (updated.daily_verification_checks as any)[day + '_notes'] = notes;
        updated.updated_at = new Date().toISOString();
        return updated;
      }
      return p;
    });
    saveActionPlans(updatedPlans);
  };

  const handleWeeklyCheck = (planId: string, week: 'week_1' | 'week_2', passed: boolean, verifiedBy: string, notes: string) => {
    const updatedPlans = actionPlans.map(p => {
      if (p.action_id === planId) {
        const updated = { ...p };
        (updated.weekly_manager_review as any)[week + '_passed'] = passed;
        (updated.weekly_manager_review as any)[week + '_verified_by'] = verifiedBy;
        (updated.weekly_manager_review as any)[week + '_notes'] = notes;
        updated.updated_at = new Date().toISOString();
        return updated;
      }
      return p;
    });
    saveActionPlans(updatedPlans);
  };

  const handleMonthlySignOff = (planId: string, managerName: string, evidenceUrl: string) => {
    const updatedPlans = actionPlans.map(p => {
      if (p.action_id === planId) {
        const updated = { ...p };
        updated.monthly_governance_signoff.is_signed_off = true;
        updated.monthly_governance_signoff.signed_off_by_manager = managerName;
        updated.monthly_governance_signoff.sign_off_timestamp = new Date().toISOString();
        updated.monthly_governance_signoff.final_evidence_document_url = evidenceUrl;
        updated.plan_status = 'Successfully Resolved & Closed';
        updated.updated_at = new Date().toISOString();
        return updated;
      }
      return p;
    });
    saveActionPlans(updatedPlans);
    setMessage({ text: '✅ Remediation closed and signed off.', type: 'success' });
    setTimeout(() => setMessage(null), 3000);
  };

  const getOverdueStatus = (plan: CompetencyActionPlan) => {
    const now = new Date();
    const dueDates = plan.due_dates;
    if (!dueDates) return false;
    for (let i = 0; i < dueDates.daily.length; i++) {
      if (new Date(dueDates.daily[i]) < now) {
        const dayKey = `day_${i+1}`;
        if (!(plan.daily_verification_checks as any)[dayKey + '_signed']) return true;
      }
    }
    for (let i = 0; i < dueDates.weekly.length; i++) {
      if (new Date(dueDates.weekly[i]) < now) {
        const weekKey = `week_${i+1}`;
        if (!(plan.weekly_manager_review as any)[weekKey + '_passed']) return true;
      }
    }
    if (dueDates.monthly && new Date(dueDates.monthly) < now) {
      if (!plan.monthly_governance_signoff.is_signed_off) return true;
    }
    return false;
  };

  // ─── RENDER ──────────────────────────────────────────────

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6 flex flex-wrap justify-between items-center">
        <div>
          <h3 className="text-xl font-bold text-[#123B36] flex items-center gap-2">
            <Clipboard className="w-6 h-6" /> Staff Competency Assessment
          </h3>
          <p className="text-[#6B7A76]">Create custom competency questions, score, and track remediation</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowNewSessionForm(true)}
            className="bg-[#123B36] text-white px-4 py-2 rounded-lg hover:bg-[#0E2C28] flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> New Assessment
          </button>
        </div>
      </div>

      {message && (
        <div className={`p-3 rounded-xl text-sm ${message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : message.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' : 'bg-blue-50 text-blue-800 border border-blue-200'}`}>
          {message.text}
        </div>
      )}

      {showNewSessionForm && (
        <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-bold text-[#123B36]">New Competency Assessment</h4>
            <button onClick={resetForm} className="text-gray-500 hover:text-red-500"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Staff ID</label>
              <VoiceInput value={newSession.interviewee_staff_id || ''} onChange={(e) => setNewSession({ ...newSession, interviewee_staff_id: e.target.value })} className="w-full border rounded px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Staff Name</label>
              <VoiceInput value={newSession.interviewee_name || ''} onChange={(e) => setNewSession({ ...newSession, interviewee_name: e.target.value })} className="w-full border rounded px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Role</label>
              <select value={newSession.interviewee_role || 'Care Assistant'} onChange={(e) => setNewSession({ ...newSession, interviewee_role: e.target.value as any })} className="w-full border rounded px-3 py-2">
                <option value="Care Assistant">Care Assistant</option>
                <option value="Domestic/Housekeeping">Domestic/Housekeeping</option>
                <option value="Kitchen Staff">Kitchen Staff</option>
                <option value="Maintenance">Maintenance</option>
                <option value="Nurse">Nurse</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Supervising Authority</label>
              <select value={newSession.supervising_authority || 'Wakefield'} onChange={(e) => setNewSession({ ...newSession, supervising_authority: e.target.value as any })} className="w-full border rounded px-3 py-2">
                <option value="Leeds">Leeds</option>
                <option value="Bradford">Bradford</option>
                <option value="Kirklees">Kirklees</option>
                <option value="Wakefield">Wakefield</option>
              </select>
            </div>
          </div>

          <div className="space-y-4">
            {(newSession.responses || []).map((resp, idx) => (
              <div key={resp.question_id} className="border border-[#E1DCCD] rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <VoiceInput
                      value={resp.question_text}
                      onChange={(e) => handleResponseChange(idx, 'question_text', e.target.value)}
                      className="w-full border-0 p-0 font-medium text-[#20302C] focus:ring-0"
                      placeholder="Enter your question..."
                    />
                  </div>
                  <div className="flex gap-1">
                    {[1,2,3,4].map(score => (
                      <button
                        key={score}
                        onClick={() => handleScoreChange(idx, score as 1|2|3|4)}
                        className={`px-2 py-1 text-xs rounded ${resp.evaluation_score === score ? 'bg-[#123B36] text-white' : 'bg-gray-200'}`}
                      >
                        {score}
                      </button>
                    ))}
                    <button onClick={() => removeQuestion(idx)} className="text-red-500 hover:text-red-700 text-xs px-1">✕</button>
                  </div>
                </div>
                <div className="mt-2">
                  <VoiceTextarea
                    placeholder="Verbatim answer..."
                    value={resp.staff_actual_answer}
                    onChange={(e) => handleResponseChange(idx, 'staff_actual_answer', e.target.value)}
                    className="w-full border rounded px-3 py-2 text-sm resize-none"
                    rows={2}
                  />
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="file"
                    multiple
                    accept="image/*,.pdf"
                    ref={(el) => { fileInputRefs.current[`file-${idx}`] = el; }}
                    onChange={(e) => handleFileUpload(idx, e)}
                    className="hidden"
                  />
                  <button onClick={() => fileInputRefs.current[`file-${idx}`]?.click()} className="text-[#123B36] text-sm flex items-center gap-1">
                    <Upload className="w-4 h-4" /> Attach Evidence
                  </button>
                  {resp.witnessed_evidence_urls.length > 0 && (
                    <span className="text-xs text-gray-500">{resp.witnessed_evidence_urls.length} file(s)</span>
                  )}
                </div>
                {resp.witnessed_evidence_urls.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {resp.witnessed_evidence_urls.map((url, fi) => (
                      <div key={fi} className="relative">
                        <img src={url} alt="evidence" className="w-16 h-16 object-cover rounded border" />
                        <button onClick={() => removeFile(idx, fi)} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 text-xs flex items-center justify-center">×</button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-2">
                  <VoiceInput
                    placeholder="Assessor feedback"
                    value={resp.assessor_feedback}
                    onChange={(e) => handleResponseChange(idx, 'assessor_feedback', e.target.value)}
                    className="w-full border rounded px-3 py-1 text-sm"
                  />
                </div>
                {resp.evaluation_score < 3 && (
                  <div className="mt-2 text-red-600 text-xs font-semibold animate-pulse">⚠️ Failed</div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-4 flex gap-2">
            <button onClick={addQuestion} className="text-[#123B36] text-sm font-semibold hover:underline">+ Add Question</button>
            <button onClick={saveInterview} className="bg-[#123B36] text-white px-6 py-2 rounded-lg hover:bg-[#0E2C28]">
              Lock and Save Assessment
            </button>
            <button onClick={resetForm} className="bg-gray-200 px-6 py-2 rounded-lg hover:bg-gray-300">Cancel</button>
          </div>
        </div>
      )}

      {showActionPlanDrawer && pendingSession && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-[#123B36]">⚡ Remedial Action Required</h3>
            <p className="text-sm text-red-600">This session has failed questions. Please complete the action plan.</p>
            <div className="mt-4 space-y-2">
              <div>
                <label className="block text-sm font-medium">Assigned Mentor Name</label>
                <VoiceInput
                  value={actionPlanForm.assigned_mentor_name || ''}
                  onChange={(e) => setActionPlanForm({ ...actionPlanForm, assigned_mentor_name: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Deficiency Details</label>
                <VoiceTextarea
                  value={actionPlanForm.deficiency_details || ''}
                  onChange={(e) => setActionPlanForm({ ...actionPlanForm, deficiency_details: e.target.value })}
                  className="w-full border rounded px-3 py-2 resize-none"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Corrective Action Required</label>
                <VoiceTextarea
                  value={actionPlanForm.corrective_action_required || ''}
                  onChange={(e) => setActionPlanForm({ ...actionPlanForm, corrective_action_required: e.target.value })}
                  className="w-full border rounded px-3 py-2 resize-none"
                  rows={3}
                />
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={saveActionPlan} className="bg-[#123B36] text-white px-6 py-2 rounded-lg hover:bg-[#0E2C28]">
                Create Action Plan & Lock Session
              </button>
              <button onClick={() => { setShowActionPlanDrawer(false); setPendingSession(null); }} className="bg-gray-200 px-6 py-2 rounded-lg hover:bg-gray-300">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── SESSIONS LIST ──────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-[#E1DCCD] overflow-hidden">
        <div className="bg-[#F7F5EE] px-6 py-3 border-b border-[#E1DCCD] flex justify-between items-center">
          <h4 className="font-bold text-[#123B36]">Competency Sessions</h4>
          <span className="text-sm text-gray-500">{sessions.length} records</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F7F5EE]">
              <tr>
                <th className="p-3 text-left">Staff</th>
                <th className="p-3 text-left">Date</th>
                <th className="p-3 text-left">Score</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-left">Locked</th>
                <th className="p-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map(s => (
                <tr key={s.session_id} className="border-b border-[#E1DCCD] hover:bg-[#F7F5EE] transition">
                  <td className="p-3">{s.interviewee_name}</td>
                  <td className="p-3">{new Date(s.date_executed).toLocaleDateString()}</td>
                  <td className="p-3">{s.session_overall_score.toFixed(0)}%</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded text-xs ${s.session_status === 'Fully Compliant (Pass)' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                      {s.session_status}
                    </span>
                  </td>
                  <td className="p-3">{s.is_locked_for_cqc ? <Lock className="w-4 h-4 text-green-600" /> : <Unlock className="w-4 h-4 text-gray-400" />}</td>
                  <td className="p-3">
                    {actionPlans.some(p => p.parent_session_id === s.session_id) && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Has Action Plan</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ─── ACTION PLANS DASHBOARD ─────────────────────────── */}
      <div className="bg-white rounded-2xl border border-[#E1DCCD] overflow-hidden">
        <div className="bg-[#F7F5EE] px-6 py-3 border-b border-[#E1DCCD] flex justify-between items-center">
          <h4 className="font-bold text-[#123B36] flex items-center gap-2">
            <Clipboard className="w-5 h-5" /> Active Remediation Plans
          </h4>
        </div>
        <div className="divide-y divide-[#E1DCCD]">
          {actionPlans.map(plan => {
            const isOverdue = getOverdueStatus(plan);
            const statusColor = plan.plan_status === 'Successfully Resolved & Closed' ? 'bg-emerald-50 border-emerald-200' :
                                isOverdue ? 'bg-red-50 border-red-200 animate-pulse' :
                                'bg-amber-50 border-amber-200';
            return (
              <div key={plan.action_id} className={`p-4 border-l-4 ${statusColor}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-[#20302C]">{plan.staff_name} – {plan.corrective_action_required.substring(0, 60)}...</p>
                    <p className="text-sm text-[#6B7A76]">Mentor: {plan.assigned_mentor_name}</p>
                    <p className="text-xs text-gray-500">Status: {plan.plan_status}</p>
                    {isOverdue && <p className="text-xs text-red-600 font-bold">🚨 Overdue – action required!</p>}
                  </div>
                </div>
                {/* Daily, Weekly, Monthly checks – same as before */}
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {(['day_1', 'day_2', 'day_3'] as const).map((day, idx) => {
                    const signed = (plan.daily_verification_checks as any)[day + '_signed'] as boolean;
                    const notes = (plan.daily_verification_checks as any)[day + '_notes'] as string;
                    const dueDate = plan.due_dates?.daily?.[idx] || 'N/A';
                    return (
                      <div key={day} className="border p-2 rounded bg-white">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium">Day {idx+1} (Due: {dueDate})</span>
                          <button
                            onClick={() => {
                              const newSigned = !signed;
                              handleDailyCheck(plan.action_id, day, newSigned, notes || `Signed by ${user?.name || 'Supervisor'}`);
                            }}
                            className={`text-xs px-2 py-0.5 rounded ${signed ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200'}`}
                          >
                            {signed ? '✅ Signed' : 'Sign'}
                          </button>
                        </div>
                        <VoiceInput
                          placeholder="Notes"
                          value={notes}
                          onChange={(e) => handleDailyCheck(plan.action_id, day, signed, e.target.value)}
                          className="w-full text-xs border rounded mt-1 px-1 py-0.5"
                        />
                      </div>
                    );
                  })}
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {(['week_1', 'week_2'] as const).map((week, idx) => {
                    const passed = (plan.weekly_manager_review as any)[week + '_passed'] as boolean;
                    const verifiedBy = (plan.weekly_manager_review as any)[week + '_verified_by'] as string;
                    const notes = (plan.weekly_manager_review as any)[week + '_notes'] as string;
                    const dueDate = plan.due_dates?.weekly?.[idx] || 'N/A';
                    return (
                      <div key={week} className="border p-2 rounded bg-white">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium">Week {idx+1} (Due: {dueDate})</span>
                          <button
                            onClick={() => {
                              const newPassed = !passed;
                              handleWeeklyCheck(plan.action_id, week, newPassed, verifiedBy || user?.name || '', notes);
                            }}
                            className={`text-xs px-2 py-0.5 rounded ${passed ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200'}`}
                          >
                            {passed ? '✅ Passed' : 'Mark Pass'}
                          </button>
                        </div>
                        <VoiceInput
                          placeholder="Verified by"
                          value={verifiedBy}
                          onChange={(e) => handleWeeklyCheck(plan.action_id, week, passed, e.target.value, notes)}
                          className="w-full text-xs border rounded mt-1 px-1 py-0.5"
                        />
                        <VoiceInput
                          placeholder="Notes"
                          value={notes}
                          onChange={(e) => handleWeeklyCheck(plan.action_id, week, passed, verifiedBy, e.target.value)}
                          className="w-full text-xs border rounded mt-1 px-1 py-0.5"
                        />
                      </div>
                    );
                  })}
                </div>
                <div className="mt-2 border p-2 rounded bg-white">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium">
                      Monthly Sign‑Off (Due: {plan.due_dates?.monthly || 'N/A'})
                    </span>
                    {plan.monthly_governance_signoff.is_signed_off ? (
                      <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">✅ Signed Off</span>
                    ) : (
                      <button
                        onClick={() => {
                          const manager = prompt('Enter manager name for sign‑off:');
                          if (manager) {
                            const evidence = prompt('Paste URL or upload evidence:') || '';
                            handleMonthlySignOff(plan.action_id, manager, evidence);
                          }
                        }}
                        className="text-xs bg-[#123B36] text-white px-2 py-0.5 rounded"
                      >
                        Sign Off
                      </button>
                    )}
                  </div>
                  {plan.monthly_governance_signoff.is_signed_off && (
                    <p className="text-xs text-gray-500 mt-1">
                      Signed by: {plan.monthly_governance_signoff.signed_off_by_manager} at {plan.monthly_governance_signoff.sign_off_timestamp}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ─── MAIN CQC READINESS MODULE ─────────────────────────────

export function CqcReadinessModule({ user }: { user: any }) {
  const [items, setItems] = useState<ReadinessItem[]>([]);
  const [activeTab, setActiveTab] = useState<'readiness' | 'mock-interview' | 'competency'>('readiness');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const stored = localStorage.getItem('rmacs_cqc_readiness');
    if (stored) {
      try { setItems(JSON.parse(stored)); } catch { setItems(DEFAULT_ITEMS); }
    } else {
      setItems(DEFAULT_ITEMS);
      localStorage.setItem('rmacs_cqc_readiness', JSON.stringify(DEFAULT_ITEMS));
    }
  }, []);

  const getStatusColor = (status: string) => {
    const map: any = {
      'Completed': 'bg-green-100 text-green-800',
      'In Progress': 'bg-amber-100 text-amber-800',
      'Not Started': 'bg-gray-100 text-gray-600',
      'Overdue': 'bg-red-100 text-red-800',
    };
    return map[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status: string) => {
    if (status === 'Completed') return <CheckCircle className="w-4 h-4 text-green-600" />;
    if (status === 'In Progress') return <Clock className="w-4 h-4 text-amber-600" />;
    if (status === 'Overdue') return <AlertTriangle className="w-4 h-4 text-red-600" />;
    return <FileText className="w-4 h-4 text-gray-400" />;
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6 flex flex-wrap justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-[#123B36] flex items-center gap-2">
            <Shield className="w-6 h-6" /> CQC Readiness Dashboard
          </h2>
          <p className="text-[#6B7A76]">Monitor compliance with CQC regulations and local authority frameworks</p>
        </div>
        <div className="flex gap-2">
          <span className="bg-[#123B36] text-white px-3 py-1 rounded-full text-sm font-semibold">
            {items.filter(i => i.status === 'Completed').length}/{items.length} Complete
          </span>
        </div>
      </div>

      <div className="flex gap-2 border-b border-[#E1DCCD] pb-2">
        <button
          onClick={() => setActiveTab('readiness')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold ${activeTab === 'readiness' ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE] text-[#123B36]'}`}
        >
          <FileCheck className="w-4 h-4 inline" /> Readiness Items
        </button>
        <button
          onClick={() => setActiveTab('mock-interview')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold ${activeTab === 'mock-interview' ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE] text-[#123B36]'}`}
        >
          <Award className="w-4 h-4 inline" /> Mock Interview
        </button>
        <button
          onClick={() => setActiveTab('competency')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold ${activeTab === 'competency' ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE] text-[#123B36]'}`}
        >
          <Clipboard className="w-4 h-4 inline" /> Staff Competency
        </button>
      </div>

      {message && (
        <div className={`p-3 rounded-xl text-sm ${message.includes('saved') || message.includes('updated') ? 'bg-[#E4F3EC] text-[#1E7F5C]' : 'bg-[#FBE4E4] text-[#C22E2E]'}`}>
          {message}
        </div>
      )}

      {activeTab === 'readiness' && (
        <div className="bg-white rounded-2xl border border-[#E1DCCD] overflow-hidden">
          <div className="bg-[#F7F5EE] px-6 py-3 border-b border-[#E1DCCD] flex justify-between items-center">
            <h3 className="font-bold text-[#123B36]">CQC Compliance Checklist</h3>
            <span className="text-sm text-gray-500">{items.length} items</span>
          </div>
          <div className="divide-y divide-[#E1DCCD]">
            {items.map(item => (
              <div key={item.id} className="p-4 hover:bg-[#F7F5EE] transition flex items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-[#20302C]">{item.title}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(item.status)}`}>
                      {item.status}
                    </span>
                  </div>
                  <p className="text-sm text-[#6B7A76] mt-1">{item.description}</p>
                  <div className="flex items-center gap-4 mt-1 text-xs text-[#6B7A76]">
                    <span>Category: {item.category}</span>
                    <span>Last updated: {item.lastUpdated}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusIcon(item.status)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'mock-interview' && (
        <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6 space-y-6">
          <div>
            <h3 className="text-xl font-bold text-[#123B36] flex items-center gap-2">
              <span className="text-2xl">📋</span> Mock CQC / Local Authority Interview Questionnaire
            </h3>
            <p className="text-sm text-[#6B7A76] mt-1">
              This resource helps your housekeeping team prepare for inspections by the Care Quality Commission (CQC) or infection control teams from Leeds, Bradford, Kirklees, and Wakefield councils.
            </p>
          </div>
          {/* Questions 1-5 – same as before */}
          <div className="border-l-4 border-[#123B36] pl-4">
            <p className="font-semibold text-[#20302C]">Question 1:</p>
            <p className="text-sm text-gray-700 italic">"Can you explain how you prevent cross‑contamination when moving from a resident’s bedroom to their en‑suite bathroom?"</p>
            <div className="mt-2 bg-[#F7F5EE] p-3 rounded-lg">
              <p className="text-sm font-medium text-[#123B36]">Target Response (Outstanding):</p>
              <p className="text-sm text-gray-700">"I strictly follow the national COSHH and BICSc colour‑coding system embedded in our digital schedules. I use <strong>Blue or Green</strong> equipment for the general bedroom surfaces and switch entirely to <strong>Red‑coded</strong> equipment (cloths, mops, buckets, and bottles) for the en‑suite washroom and toilet zones. I also discard my disposable apron and gloves and perform full hand hygiene before moving between these distinct hygiene zones."</p>
            </div>
          </div>
          <div className="border-l-4 border-[#123B36] pl-4">
            <p className="font-semibold text-[#20302C]">Question 2:</p>
            <p className="text-sm text-gray-700 italic">"What chemical do you use for routine daily cleaning versus a suspected Norovirus or respiratory outbreak, and why?"</p>
            <div className="mt-2 bg-[#F7F5EE] p-3 rounded-lg">
              <p className="text-sm font-medium text-[#123B36]">Target Response (Outstanding):</p>
              <p className="text-sm text-gray-700">"For routine cleaning, we use a neutral detergent or a biological washroom cleaner. However, if a resident shows symptoms of a gastrointestinal or respiratory outbreak, our digital care system alerts us to immediately escalate to an <strong>EN 14476 certified virucidal disinfectant</strong> or a <strong>1,000 ppm chlorine‑releasing solution</strong>. I ensure the solution is left on surfaces for the exact manufacturer contact dwell time to effectively destroy viral pathogens."</p>
            </div>
          </div>
          <div className="border-l-4 border-[#123B36] pl-4">
            <p className="font-semibold text-[#20302C]">Question 3:</p>
            <p className="text-sm text-gray-700 italic">"If you run out of a specific pre‑mixed cleaning chemical mid‑shift, can you decant a concentrate into an empty trigger spray bottle?"</p>
            <div className="mt-2 bg-[#F7F5EE] p-3 rounded-lg">
              <p className="text-sm font-medium text-[#123B36]">Target Response (Outstanding):</p>
              <p className="text-sm text-gray-700">"No, I would never use an unlabelled or improvised container. Under COSHH regulations and our Yorkshire audit standards, any decanted chemical must strictly use a dedicated, pre‑printed <strong>Bottle for Life</strong> or matching container that clearly displays the product name, hazard pictograms, correct dilution level, and PPE requirements. If one isn't available, I must request a pre‑measured sachet or replacement from the locked COSHH store."</p>
            </div>
          </div>
          <div className="border-l-4 border-[#123B36] pl-4">
            <p className="font-semibold text-[#20302C]">Question 4:</p>
            <p className="text-sm text-gray-700 italic">"Where do you find the safety instructions if a chemical accidentally splashes into your eyes or onto your skin?"</p>
            <div className="mt-2 bg-[#F7F5EE] p-3 rounded-lg">
              <p className="text-sm font-medium text-[#123B36]">Target Response (Outstanding):</p>
              <p className="text-sm text-gray-700">"Every chemical we use has a live <strong>Safety Data Sheet (SDS)</strong> stored in the digital care portal and physically inside the master folder in the locked central housekeeping store. I know exactly where the emergency eye wash stations are located, and I would immediately check the SDS section for first‑aid instructions before logging the incident in the system's digital accident book."</p>
            </div>
          </div>
          <div className="border-l-4 border-[#123B36] pl-4">
            <p className="font-semibold text-[#20302C]">Question 5:</p>
            <p className="text-sm text-gray-700 italic">"What do you do if you notice a resident's waterproof mattress cover is torn or stained during your cleaning routine?"</p>
            <div className="mt-2 bg-[#F7F5EE] p-3 rounded-lg">
              <p className="text-sm font-medium text-[#123B36]">Target Response (Outstanding):</p>
              <p className="text-sm text-gray-700">"I would immediately unzip the cover to inspect the internal foam core for any fluid ingress. If there is staining, the mattress is failed and taken out of service to prevent deep contamination. I immediately log the defect into our digital maintenance portal with a photo, inform the nurse‑in‑charge to protect the resident, and record the action taken to close the audit loop."</p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'competency' && (
        <StaffCompetency user={user} />
      )}
    </div>
  );
}

export default CqcReadinessModule;